import type {Engine} from '@babylonjs/core/Engines/engine.js';
import type {Camera} from '@babylonjs/core/Cameras/camera.js';
/** Bounded stabilization bursts, no permanent RAF/resize polling. */
export class ViewportSync{
 private frame=0;private attempts=0;private stable=0;private last='';private timers:number[]=[];
 private observer:ResizeObserver;private cleanup:(()=>void)[]=[];updates=0;requests=0;
 constructor(private engine:Engine,private canvas:HTMLCanvasElement,private camera:()=>Camera|null){
  const listen=(target:EventTarget,name:string)=>{target.addEventListener(name,this.schedule);this.cleanup.push(()=>target.removeEventListener(name,this.schedule));};
  listen(window,'resize');window.addEventListener('resize',this.delayedOrientation);this.cleanup.push(()=>window.removeEventListener('resize',this.delayedOrientation));listen(window,'pageshow');
  if(window.visualViewport){listen(window.visualViewport,'resize');listen(window.visualViewport,'scroll');}
  window.addEventListener('orientationchange',this.refresh);this.cleanup.push(()=>window.removeEventListener('orientationchange',this.refresh));
  screen.orientation?.addEventListener('change',this.refresh);this.cleanup.push(()=>screen.orientation?.removeEventListener('change',this.refresh));
  listen(document,'fullscreenchange');
  this.observer=new ResizeObserver(()=>this.schedule());this.observer.observe(canvas);
  this.refresh();
 }
 private dimensions(){
  const v=window.visualViewport;
  return {width:Math.max(1,v?.width||window.innerWidth),height:Math.max(1,v?.height||window.innerHeight)};
 }
 private sync(force=false){
  const v=this.dimensions();
  const style=document.documentElement.style;
  const width=v.width.toFixed(3)+'px',height=v.height.toFixed(3)+'px';
  if(style.getPropertyValue('--game-width')!==width)style.setProperty('--game-width',width);
  if(style.getPropertyValue('--game-height')!==height)style.setProperty('--game-height',height);
  const rect=this.canvas.getBoundingClientRect(),scale=this.engine.getHardwareScalingLevel();
  const w=Math.max(1,Math.round(rect.width/scale)),h=Math.max(1,Math.round(rect.height/scale));
  if(force||this.engine.getRenderWidth()!==w||this.engine.getRenderHeight()!==h){
   this.engine.setSize(w,h,true);this.updates++;
  }
  // Recompute from the final render aspect even if Babylon cached an earlier viewport.
  this.camera()?.getProjectionMatrix(true);
  return [rect.width,rect.height,w,h,scale].join(',');
 }
 private tick=()=>{
  this.frame=0;const key=this.sync();this.stable=key===this.last?this.stable+1:0;this.last=key;
  if(++this.attempts<12&&this.stable<2)this.frame=requestAnimationFrame(this.tick);
 };
 schedule=()=>{
  this.requests++;if(this.frame)return;
  this.attempts=0;this.stable=0;this.frame=requestAnimationFrame(this.tick);
 };
 refresh=()=>{
  this.sync();this.schedule();
  for(const timer of this.timers)clearTimeout(timer);
  // iOS can deliver final layout after orientationchange/resize, not in that callback.
  this.timers=[80,250,600].map(ms=>window.setTimeout(()=>this.schedule(),ms));
 };
 private delayedOrientation=()=>this.refresh();
 snapshot(){
  const rect=this.canvas.getBoundingClientRect(),c=this.camera(),v=this.dimensions(),p=c?.getProjectionMatrix();
  return {viewport:[v.width,v.height],layoutViewport:[innerWidth,innerHeight],css:[rect.width,rect.height],buffer:[this.engine.getRenderWidth(),this.engine.getRenderHeight()],aspect:c?this.engine.getAspectRatio(c):null,projectionAspect:p?p.m[5]/p.m[0]:null,dpr:devicePixelRatio,hardwareScaling:this.engine.getHardwareScalingLevel(),updates:this.updates,requests:this.requests,pending:Boolean(this.frame)};
 }
 dispose(){cancelAnimationFrame(this.frame);for(const t of this.timers)clearTimeout(t);this.observer.disconnect();for(const f of this.cleanup)f();}
}
